import cx from "classnames";
import { useState } from "react";

import { useTranslation } from "@/core/i18n";
import { Models } from "api/types";
import ArrowLeft from "atoms/icon/icons/arrow_left.svg";
import ArrowRight from "atoms/icon/icons/arrow_right.svg";
import Close from "atoms/icon/icons/close.svg";
import { IconButton } from "atoms/icon-button/IconButton";
import { Modal } from "atoms/modal/Modal";
import { SimpleCarousel } from "atoms/simple-carousel/SimpleCarousel";

type BeforeAfterPair = Models<"DisplayPatientTreatmentBeforeAfterPairResponse">;

type BeforeAfterGalleryProps = {
  "pairs": BeforeAfterPair[];
  "presentation"?: "pdp";
  "data-testid"?: string;
};

const PAIR_ASPECT_RATIO = "4/3";

/** Same near-fullscreen sheet height the app's other fullscreen modals use. */
const EXPANDED_SHEET_HEIGHT = "h-[calc(100%_-_env(safe-area-inset-top)_-_12px)]";

type GalleryPhoto = { src: string; label: string; alt: string };

const PairImage = ({
  photo,
  onExpand,
  expandLabel,
}: {
  photo: GalleryPhoto;
  onExpand: () => void;
  expandLabel: string;
}) => (
  <button
    aria-label={expandLabel}
    className="relative min-w-0 flex-1"
    type="button"
    onClick={onExpand}
  >
    <img
      alt={photo.alt}
      className="h-full w-full rounded bg-two object-contain"
      draggable={false}
      src={photo.src}
    />
    <span className="pointer-events-none absolute left-1.5 top-1.5 rounded bg-black/55 px-1.5 py-0.5 text-sub2 text-white">
      {photo.label}
    </span>
  </button>
);

/**
 * Results gallery for a treatment PDP. The pairs arrive pre-ordered by the API
 * (most demographically similar to the requesting patient first) and are rendered
 * in exactly that order — never re-sorted client side.
 */
export const BeforeAfterGallery = ({
  pairs,
  presentation,
  "data-testid": dataTestId,
}: BeforeAfterGalleryProps) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  // Index into `photos`, not into `pairs`: the enlarged view is one photo at a time and
  // runs through every photo in the gallery rather than stopping at the pair it opened from.
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  if (pairs.length === 0) return null;

  const photos: GalleryPhoto[] = pairs.flatMap((pair) => [
    {
      src: pair.beforeUrl,
      label: t("beforeAfterGallery.before"),
      alt: t("beforeAfterGallery.beforeImageAlt"),
    },
    {
      src: pair.afterUrl,
      label: t("beforeAfterGallery.after"),
      alt: t("beforeAfterGallery.afterImageAlt"),
    },
  ]);

  const expandedPhoto = expandedIndex == null ? null : photos[expandedIndex];
  const step = (delta: number) =>
    setExpandedIndex((index) =>
      index == null ? index : (index + delta + photos.length) % photos.length,
    );

  return (
    <div
      className={cx("flex flex-col gap-3", presentation === "pdp" && "h-full min-h-0 py-4")}
      data-testid={dataTestId}
    >
      {presentation !== "pdp" && (
        <div className="text-center text-sub1 text-brand-color">
          {t("beforeAfterGallery.title")}
        </div>
      )}

      <SimpleCarousel
        fillHeight={presentation === "pdp"}
        className={presentation === "pdp" ? "overflow-hidden rounded-2xl !pb-0" : undefined}
        slideClassName={presentation === "pdp" ? "!px-0" : undefined}
        previousLabel={t("beforeAfterGallery.previousPhoto")}
        nextLabel={t("beforeAfterGallery.nextPhoto")}
        aspectRatio={PAIR_ASPECT_RATIO}
        slides={pairs.map((pair, pairIndex) => (
          <div className="flex h-full w-full gap-2" key={pair.id}>
            <PairImage
              expandLabel={t("beforeAfterGallery.enlargePhoto")}
              photo={photos[pairIndex * 2]}
              onExpand={() => setExpandedIndex(pairIndex * 2)}
            />
            <PairImage
              expandLabel={t("beforeAfterGallery.enlargePhoto")}
              photo={photos[pairIndex * 2 + 1]}
              onExpand={() => setExpandedIndex(pairIndex * 2 + 1)}
            />
          </div>
        ))}
        onUpdateIndex={setCurrentIndex}
      />

      <div className="flex flex-col items-center gap-0.5 text-body3 text-secondary">
        {pairs.length > 1 && presentation !== "pdp" && (
          <span>
            {t("beforeAfterGallery.pairPosition", {
              current: currentIndex + 1,
              total: pairs.length,
            })}
          </span>
        )}
        <span>{t("beforeAfterGallery.tapToEnlarge")}</span>
      </div>

      {/* Modal is a bottom sheet sized by `size`, not by `fullScreen` (which only adjusts footer
          padding). Without an explicit height the sheet collapses to its content and the h-full
          image inside has nothing to resolve against. */}
      <Modal
        bodyClassName="flex"
        header={
          <div className="flex h-[56px] w-full items-center justify-between border-b border-light-grey px-3">
            <IconButton
              aria-label={t("beforeAfterGallery.closeEnlarged")}
              svg={Close}
              size="small"
              style="iconOnly"
              onClick={() => setExpandedIndex(null)}
            />
            <span className="text-body3 text-secondary">
              {t("beforeAfterGallery.pairPosition", {
                current: (expandedIndex ?? 0) + 1,
                total: photos.length,
              })}
            </span>
            <span className="w-8" />
          </div>
        }
        open={expandedPhoto != null}
        size={`${EXPANDED_SHEET_HEIGHT} desk:!h-[min(80vh,720px)] desk:!w-[min(80vw,960px)]`}
        onClose={() => setExpandedIndex(null)}
      >
        {expandedPhoto != null && (
          <div className="relative flex min-h-0 w-full grow items-center justify-center bg-black">
            <img
              alt={expandedPhoto.alt}
              className="max-h-full max-w-full object-contain"
              draggable={false}
              src={expandedPhoto.src}
            />

            <span className="pointer-events-none absolute left-3 top-3 rounded bg-black/55 px-2 py-1 text-sub2 text-white">
              {expandedPhoto.label}
            </span>

            {photos.length > 1 && (
              <>
                <div className="absolute left-2 top-1/2 -translate-y-1/2">
                  <IconButton
                    aria-label={t("beforeAfterGallery.previousPhoto")}
                    svg={ArrowLeft}
                    size="small"
                    style="camera"
                    onClick={() => step(-1)}
                  />
                </div>
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <IconButton
                    aria-label={t("beforeAfterGallery.nextPhoto")}
                    svg={ArrowRight}
                    size="small"
                    style="camera"
                    onClick={() => step(1)}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};
